import type { Product } from "../types/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/client";
import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { MagnifyingGlassIcon, NoSymbolIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../store/authStore";
import { Switch } from "@headlessui/react";

export default function Products() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [includeZeroValue, setIncludeZeroValue] = useState(false);
  const { role } = useAuthStore();
  const isSuperAdmin = role === 'super_admin';
  const limit = 50;

  // Debounce search and reset page
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["products", includeZeroValue, page, debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams({
        includeZeroValue: includeZeroValue.toString(),
        page: page.toString(),
        limit: limit.toString(),
        ...(debouncedSearch && { search: debouncedSearch }),
      });
      const res = await api.get(`/admin/products?${params.toString()}`);
      return res.data.data;
    },
  });

  const blacklistMutation = useMutation({
    mutationFn: async ({ asin, blacklist }: { asin: string; blacklist: boolean }) => {
      return api.post("/admin/products/blacklist", { asin, blacklist });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product updated successfully");
    },
    onError: () => {
      toast.error("Failed to update product");
    },
  });

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading products</div>;

  const products = data?.products || [];
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate sm:text-3xl sm:tracking-tight">
            Products
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {pagination.total} unique products across all claimants
          </p>
        </div>
        {isSuperAdmin && (
          <div className="mt-4 flex md:ml-4 md:mt-0">
             <Switch.Group as="div" className="flex items-center">
                <Switch
                  checked={includeZeroValue}
                  onChange={(value) => { setIncludeZeroValue(value); setPage(1); }}
                  className={`${
                    includeZeroValue ? 'bg-indigo-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2`}
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      includeZeroValue ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </Switch>
                <Switch.Label as="span" className="ml-3 text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">Include 0€ Items</span>
                </Switch.Label>
              </Switch.Group>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by ASIN or product name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Showing {products.length} of {pagination.total} products (Page {pagination.page} of {pagination.totalPages})
        </p>
      </div>

      {/* Products Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-6">ASIN</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Product Name</th>
              <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-white">Order Count</th>
              <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-white">Min Price</th>
              <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-white">Max Price</th>
              <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-white">Total GMV</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
              <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {products.map((product: Product) => (
              <tr key={product.asin} className={product.blacklisted ? "bg-red-50 dark:bg-red-900/20" : ""}>
                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-white sm:pl-6">
                  {product.asin}
                </td>
                <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md truncate" title={product.productName}>
                  {product.productName}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
                  {product.orderCount}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
                  €{product.minPrice?.toFixed(2) || '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
                  €{product.maxPrice?.toFixed(2) || '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
                  €{product.totalGMV?.toFixed(2) || '0.00'}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm">
                  {product.blacklisted ? (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                      <NoSymbolIcon className="h-3 w-3 mr-1" />
                      Blacklisted
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  )}
                </td>
                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                  <button
                    onClick={() =>
                      blacklistMutation.mutate({
                        asin: product.asin,
                        blacklist: !product.blacklisted,
                      })
                    }
                    disabled={blacklistMutation.isPending}
                    className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed ${
                      product.blacklisted
                        ? "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                        : "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
                    }`}
                  >
                    {product.blacklisted ? "Unblock" : "Blacklist"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {products.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {debouncedSearch ? `No products found matching "${debouncedSearch}"` : 'No products found'}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-lg shadow">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                <span className="font-medium">{pagination.totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 focus:z-20 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  className="relative inline-flex items-center rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 focus:z-20 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
